import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Controller, type UseFormReturn } from 'react-hook-form'
import { useQuery } from '@tanstack/react-query'
import {
  columnsForHeader, formBlocks, gridKeyOf, isFieldReadOnly, isFieldVisible, type DocumentDef, type DocumentStatus, type FieldDef, type FormBlock, type LineColumnDef, type LineGridDef,
} from '@project/contracts'
import { Button, Icon, Panel } from '../design/index.js'
import { useApi, type DocumentDetail, type LineRow } from '../app/api.js'
import { TotalValuePanel } from './TotalValuePanel.js'
import { FormField } from './controls.js'
import { asText } from './format.js'
import { LineGrid, type PickableRow } from './LineGrid.js'
import { LineSources } from './LineSources.js'
import { BillingTerms } from './BillingTerms.js'
import { useSettings } from './queries.js'
import { LiveProjectFigures } from './ProjectFigures.js'
import { gridRows, newRowFor } from './grid-rows.js'
import { useProjectCustomerDefaults } from './project-form.js'

export type DocumentForm = UseFormReturn<Record<string, unknown>>

export interface FormTabProps {
  readonly def: DocumentDef
  readonly form: DocumentForm
  readonly status: DocumentStatus
  readonly lines: readonly LineRow[]
  readonly fieldLabels: Readonly<Record<string, string>>
  readonly documentId: string
  readonly onLinesChange: (lines: readonly LineRow[]) => void
  readonly readOnly: boolean
  /** A rate cell's history icon opens the price history at that line (PUR-039). */
  readonly onRateHistory?: (lineId: string) => void
  /** The saved document: its currency, tax unit and Total Value (BILL-009, BILL-013). */
  readonly doc?: DocumentDetail
  /**
   * Fields still open on an approved document — `editableAfterApproval` —
   * are open while this is set (the header Update action).
   */
  readonly approvedEditable?: boolean
  /** Unsaved edits on screen — an import warns before leaving them behind. */
  readonly dirty?: boolean
  /**
   * The tax unit chip, clicked: Action › Change the tax unit, where the
   * document offers it. Without it the chip only reads.
   */
  readonly onChangeTaxUnit?: () => void
  /** The currency chip, clicked: Action › Change the currency. */
  readonly onChangeCurrency?: () => void
  /**
   * Panels the page hands in, drawn where `def.formOrder` names them
   * (`'slot:projectTeam'`). A slot the order does not name is not drawn here.
   */
  readonly slots?: Readonly<Record<string, ReactNode>>
}


/**
 * The label a picker shows for one row of another grid.
 *
 * A BOQ line reads as its reference and description; a billing term as its
 * name and the line under it. Falls back to the row number, so a row that has
 * been added and not yet filled in is still choosable.
 */
const rowLabel = (row: LineRow, index: number): string => {
  const parts = [
    asText(row['boqCode']) || asText(row['termName']),
    asText(row['itemLabel']) || asText(row['itemName']) || asText(row['description']),
  ].filter((p) => p !== '')
  return parts.length > 0 ? parts.join(' — ') : `Row ${index + 1}`
}

/**
 * The rows a picker column may choose from.
 *
 * A saved row is chosen by its id. One added in this same edit has no id yet,
 * so it is chosen by the `seq:<SECTION>:<n>` key the save path resolves to a
 * real id once both rows exist — which is what lets a BOQ line and the estimate
 * under it be typed in one sitting.
 */
const pickableFrom = (lines: readonly LineRow[], section: string): PickableRow[] =>
  lines
    .filter((l) => String(l['section'] ?? '') === section)
    .map((l, index) => ({
      value: asText(l['id']) || `seq:${section}:${asText(l['seq']) || index + 1}`,
      label: rowLabel(l, index),
    }))

/**
 * The whole editable document, rendered from `def.form.sections`, `def.lines`
 * and `def.grids`. Section numbers come from the definition so the screen and
 * the printed PDF are numbered identically.
 *
 * Every grid the definition declares is drawn. They were not, and the ones that
 * were missing were the ones the product is about: a project's estimate and its
 * billing terms, the suppliers invited to an RFQ, and the additional charges and
 * deductions on nine document types. All of it was stored, computed and
 * validated by the server, and none of it could be entered.
 */
export const FormTab = ({
  def, form, status, lines, fieldLabels, onLinesChange, readOnly, documentId, doc, approvedEditable = false, dirty = false,
  onRateHistory, onChangeTaxUnit, onChangeCurrency, slots,
}: FormTabProps) => {
  const api = useApi()
  const leftDraft = status !== 'DRAFT'
  const settings = useSettings()
  const values = settings.data?.values

  const primarySection = def.lines?.section ?? 'ITEM'

  /** One grid's rows. Everything is one list; `section` is what separates them. */
  const rowsOf = (section: string): LineRow[] =>
    lines.filter((l) => String(l['section'] ?? primarySection) === section)

  /**
   * Puts one grid's rows back, leaving every other grid's alone.
   *
   * The document is saved as a single list of lines, so editing one grid has to
   * rewrite only its own slice — otherwise adding a charge would delete the
   * items beside it.
   */
  const replaceSection = (section: string, next: readonly LineRow[]): void => {
    const others = lines.filter((l) => String(l['section'] ?? primarySection) !== section)
    onLinesChange([...others, ...next.map((l) => ({ ...l, section }))])
  }

  /** What each of a grid's picker columns may choose from. */
  const pickableFor = (grid: { columns: readonly LineColumnDef[]; parentSection?: string }) => {
    const out: Record<string, PickableRow[]> = {}
    for (const column of grid.columns) {
      // Another grid's rows by their words — a BOQ line's Billing Summary Item.
      if (column.optionsFrom) {
        const from = column.optionsFrom
        const words = rowsOf(from.section).map((l) => asText(l[from.column]).trim()).filter((w) => w !== '')
        out[column.key] = [...new Set(words)].map((w) => ({ value: w, label: w }))
        continue
      }
      if (!column.parentPicker) continue
      const from = column.picksFrom ?? grid.parentSection
      if (from) out[column.key] = pickableFrom(lines, from)
    }
    return out
  }

  // A company that does not use line discounts should not be shown the column;
  // the server ignores what is typed into it either way.
  const lineColumns = useMemo(() => {
    const all = def.lines?.columns ?? []
    return values?.['line_discount_enabled'] === false
      ? all.filter((c) => c.key !== 'discountPct')
      : all
  }, [def.lines, values])

  /** The extra cells every row added to this grid starts with: its defaults, and its seq and reference. */
  const newRowOf = (grid: LineGridDef, split: boolean, extra: LineRow = {}): (() => LineRow) | undefined =>
    split || grid.autoRef ? () => ({ ...newRowFor(grid, lines), ...extra }) : undefined

  const overallDiscount = values?.['overall_discount_enabled'] === true

  // What has been answered so far, so a question that depends on another
  // appears and disappears as it is answered.
  const watched = form.watch()
  /** A grid's columns as the header answers have them — a Single Rate project's one rate (PROJ2-024). */
  const headerColumns = (columns: readonly LineColumnDef[], rows: readonly LineRow[]) => columnsForHeader(columns, watched, rows)
  // A project's addresses and consignee follow the customer chosen.
  useProjectCustomerDefaults(def, form, readOnly)

  // A field with a default (Link type → Master project) starts at it on a
  // document that has never answered it. Not marked dirty: nothing was typed.
  useEffect(() => {
    if (readOnly) return
    for (const section of def.form.sections) {
      for (const field of section.fields) {
        if (field.defaultValue === undefined) continue
        const held = form.getValues(field.key)
        if (held === undefined || held === null || held === '') form.setValue(field.key, field.defaultValue)
      }
    }
  }, [def, form, readOnly])

  /** Does the header, as answered, meet this condition (a grid's, a source's, `pickOnly`)? */
  const holds = (when: FieldDef['visibleWhen']): boolean =>
    !when || isFieldVisible({ key: '', label: '', control: 'text', visibleWhen: when }, watched, values)
  /** A grid that is one of two over its section, or hidden by a header answer. */
  const gridShown = (grid: LineGridDef): boolean => holds(grid.visibleWhen)
  /** The ways in a grid offers as the header stands — no estimate on a project order (PUR-029). */
  const sourcesOf = (grid: LineGridDef) => grid.sources.filter((x) => holds(x.visibleWhen))
  /** Rows only from the sources, with the grid's helper in place of "Add" (PUR-029). */
  const pickOnly = (grid: LineGridDef): boolean => grid.pickOnly !== undefined && holds(grid.pickOnly.when)
  const gridProps = (grid: LineGridDef) => ({
    ...(grid.emptyText ? { emptyText: grid.emptyText } : {}),
    canAdd: !pickOnly(grid),
  })

  /*
    Only the questions that are actually being asked.

    `visibleWhen` and `requiresSetting` were authored on the definitions and
    read by nobody on this side, so a stock purchase order still demanded a
    project, and the e-invoice and e-way-bill fields sat on every invoice and
    challan whether or not the company had those integrations switched on. A
    hidden field is not required either — `required` on a question nobody is
    asked is a form that cannot be submitted.
  */
  // A value the server keeps but nobody types (`importOnly`) is not drawn either.
  const visibleFields = (fields: DocumentDef['form']['sections'][number]['fields']) =>
    fields.filter((field) => field.chip !== true && field.importOnly !== true && isFieldVisible(field, watched, values))

  /*
    The currency and the tax unit, at a glance on the first card (BILL-009,
    PROJ1-004): "INR-Indian Rupee" and "Default - <company>" are chips on the
    reference's General Info, not questions further down. Any field marked
    `chip` joins them, read from the label the server resolved for it; the tax
    unit shows whether or not the form declares it, because the wizard may
    have asked it.
  */
  const currencies = useQuery({
    queryKey: ['currencies', api.companyId],
    queryFn: () => api.masterList('currency', { pageSize: '200' }),
    enabled: doc !== undefined,
    staleTime: 10 * 60_000,
  })
  const currencyName = doc
    ? asText((currencies.data?.rows ?? []).find((r) => asText(r['code']) === doc.currency.code)?.['name'])
    : ''
  const chipFields = def.form.sections.flatMap((s) => s.fields).filter((f) => f.chip === true)
  const chipKeys = [...new Set(['taxUnitId', ...chipFields.map((f) => f.key)])]
  // Clickable only when the page offers the action behind it right now.
  const chip = (key: string, text: string, title: string, onClick?: () => void) => onClick
    ? (
      <button key={key} type="button" className="chip chip--action" title={title} onClick={onClick}>{text}</button>
    ) : <span key={key} className="chip" title={title}>{text}</span>
  const badges = doc ? (
    <span className="row" style={{ gap: 6 }}>
      {chip('currency', currencyName ? `${doc.currency.code}-${currencyName}` : doc.currency.code,
        onChangeCurrency ? 'Currency. Click to change it.' : 'Currency', onChangeCurrency)}
      {chipKeys.map((key) => {
        const text = fieldLabels[key]
        if (!text) return null
        const label = chipFields.find((f) => f.key === key)?.label ?? 'Tax unit'
        const onClick = key === 'taxUnitId' ? onChangeTaxUnit : undefined
        return chip(key, text, onClick ? `${label}. Click to change it.` : label, onClick)
      })}
    </span>
  ) : undefined

  /*
    Numbering switched off for the type (SET-023): the number is typed, so the
    first section asks for it where the type's own form has no box for it.
  */
  const sections = doc?.numberingOff === true && status === 'DRAFT'
    ? def.form.sections.map((section, index) => index !== 0 ? section : {
      ...section,
      fields: [
        { key: 'documentNo', label: 'Document number', control: 'text' as const, width: 'half' as const, required: true,
          hint: 'Automatic numbering is switched off for this type.' },
        ...section.fields,
      ],
    })
    : def.form.sections

  const firstSection = sections[0]?.key

  const renderSection = (section: DocumentDef['form']['sections'][number]) => (
    <Panel key={section.key} n={section.n} title={section.title} aside={section.key === firstSection ? badges : undefined}>
      <div className="field-grid">
        {visibleFields(section.fields).map((field) => (
          <Controller
            key={field.key}
            name={field.key}
            control={form.control}
            rules={{ required: field.required === true ? `${field.label} is required.` : false }}
            render={({ field: bound, fieldState }) => (
              <FormField
                field={field}
                value={bound.value ?? ''}
                valueLabel={fieldLabels[field.key]}
                scopeValues={watched}
                onChange={bound.onChange}
                disabled={(readOnly && !(approvedEditable && field.editableAfterApproval === true))
                  || isFieldReadOnly(field, watched)
                  || (field.lockAfterDraft === true && leftDraft)}
                error={fieldState.error?.message}
              />
            )}
          />
        ))}
      </div>
    </Panel>
  )

  const renderPrimary = (grid: LineGridDef) => (
    <Panel
      key={`grid:${gridKeyOf(grid)}`}
      id={`grid-${primarySection}`}
      n={grid.n}
      title={grid.title}
      aside={`${lines.length} line${lines.length === 1 ? '' : 's'}`}
    >
      {pickOnly(grid) && !readOnly && grid.pickOnly ? <p className="helper" style={{ marginBottom: 12 }}>{grid.pickOnly.helper}</p> : null}
      {documentId && (!readOnly || def.key === 'project') ? (
        <LineSources
          // Remounted when the form locks: the export-only branch has no hooks.
          key={readOnly ? 'export' : 'edit'}
          docKey={def.key}
          documentId={documentId}
          sources={sourcesOf(grid)}
          lines={lines}
          onAdd={onLinesChange}
          exportOnly={readOnly}
          dirty={dirty}
        />
      ) : null}
      {grid.exportable && documentId ? (
        <a className="btn btn--ghost" style={{ marginBottom: 16 }} download
          href={api.gridExportUrl(def.key, documentId, primarySection)}>
          <Icon name="dl" size={18} />
          <span>{grid.exportLabel ?? 'Export to Excel'}</span>
        </a>
      ) : null}
      {/* "Select items" over the same lines replaces the pull-everything button (BILL-038, INV-012). */}
      {!readOnly && def.allocatesFrom?.length && documentId
        && !grid.sources.some((x) => x.source === 'billing' || x.source === 'order') ? (
        <PullSourceLines
          docKey={def.key}
          documentId={documentId}
          label="Pull the open lines"
          existing={lines}
          onPulled={onLinesChange}
        />
      ) : null}
      <LineGrid
        columns={headerColumns(lineColumns, rowsOf(primarySection))}
        lines={rowsOf(primarySection)}
        readOnly={readOnly}
        onChange={(next) => replaceSection(primarySection, next)}
        addLabel={grid.addLabel}
        newRow={newRowOf(grid, false)}
        section={primarySection}
        pickable={pickableFor(grid)}
        addOpensDialog={grid.addOpensDialog === true}
        {...dialogProps(grid)}
        {...gridProps(grid)}
        {...(nestedUnder(primarySection) ? { rowBelow: nestedRows(nestedUnder(primarySection) as LineGridDef) } : {})}
        {...(onRateHistory ? { onRateHistory } : {})}
      />
      {overallDiscount ? (
        <div className="overall-discount">
          <label className="field__label" htmlFor="overall-discount">Discount on the whole document (%)</label>
          <input
            id="overall-discount"
            className="control"
            style={{ width: 140 }}
            inputMode="decimal"
            disabled={readOnly}
            value={String(form.watch('overallDiscountPct') ?? '')}
            onChange={(e) => form.setValue('overallDiscountPct', e.currentTarget.value, { shouldDirty: true })}
          />
          <p className="field__hint">
            Spread across the lines, so tax is charged on the discounted value of each.
          </p>
        </div>
      ) : null}
    </Panel>
  )

  /** What a grid tells its row dialog and its new rows. */
  const dialogProps = (grid: LineGridDef) => ({
    ...(grid.detailTitle ? { detailTitle: grid.detailTitle } : {}),
    ...(grid.detailAsksFirst ? { detailAsksFirst: grid.detailAsksFirst } : {}),
    ...(grid.autoRef ? { detailRef: grid.autoRef.column } : {}),
    ...(grid.rowDefaults ? { rowDefaults: grid.rowDefaults } : {}),
  })

  /** The grid drawn under each row of this section's grid, if one is (`nested`). */
  const nestedUnder = (section: string): LineGridDef | undefined =>
    (def.grids ?? []).find((g) => g.nested === true && g.parentSection === section && gridShown(g))

  /*
    A BOQ line's estimate, under the BOQ line (PROJ1-008). Each parent row
    is named the way a picker names it — its id once saved, its seq key
    before — and its rows are the nested grid's rows that point at it.
  */
  const nestedRows = (grid: LineGridDef) => (parent: LineRow): ReactNode => {
    if (parent['lineType'] === 'SECTION') return null
    const parentSection = grid.parentSection ?? primarySection
    const id = asText(parent['id'])
    const seqKey = `seq:${parentSection}:${asText(parent['seq'])}`
    const section = grid.section ?? 'ITEM'
    const all = gridRows(grid, rowsOf(section))
    const own = all.filter((l) => {
      const p = asText(l['parentLineId'])
      return p !== '' && (p === id || p === seqKey)
    })
    if (readOnly && own.length === 0) return null
    return (
      <NestedGrid
        key={id || seqKey}
        title={grid.title}
        count={own.length}
      >
        <LineGrid
          columns={headerColumns(grid.columns, own)}
          lines={own}
          readOnly={readOnly}
          onChange={(next) => replaceSection(section, [
            ...rowsOf(section).filter((l) => !own.includes(l)),
            ...next,
          ])}
          addLabel={grid.addLabel}
          section={section}
          pickable={pickableFor(grid)}
          newRow={newRowOf(grid, true, { parentLineId: id || seqKey })}
          addOpensDialog={grid.addOpensDialog === true}
          {...dialogProps(grid)}
        />
      </NestedGrid>
    )
  }

  const renderGrid = (grid: LineGridDef) => {
    if (!gridShown(grid)) return null
    const section = grid.section ?? 'ITEM'
    const rows = gridRows(grid, rowsOf(section))
    const split = grid.rows !== undefined
    return (
      <Panel
        key={`grid:${gridKeyOf(grid)}`}
        id={`grid-${grid.key ?? section}`}
        n={grid.n}
        title={grid.title}
        aside={`${rows.length} line${rows.length === 1 ? '' : 's'}`}
      >
        {pickOnly(grid) && !readOnly && grid.pickOnly ? <p className="helper" style={{ marginBottom: 12 }}>{grid.pickOnly.helper}</p> : null}
        {!readOnly && documentId && sourcesOf(grid).length > 0 ? (
          <LineSources
            docKey={def.key}
            documentId={documentId}
            sources={sourcesOf(grid)}
            lines={lines}
            onAdd={onLinesChange}
            section={section}
            dirty={dirty}
          />
        ) : null}
        {grid.editor === 'billingTerms' ? (
          <BillingTerms lines={rows} readOnly={readOnly} onChange={(next) => replaceSection(section, next)} />
        ) : (
        <LineGrid
          columns={headerColumns(grid.columns, rows)}
          lines={rows}
          readOnly={readOnly}
          onChange={(next) => (split
            // Only this grid's share of the section is replaced; the other
            // grid over it keeps its rows.
            ? replaceSection(section, [...rowsOf(section).filter((l) => !rows.includes(l)), ...next])
            : replaceSection(section, next))}
          addLabel={grid.addLabel}
          section={section}
          pickable={pickableFor(grid)}
          newRow={newRowOf(grid, split)}
          addOpensDialog={grid.addOpensDialog === true}
          {...dialogProps(grid)}
          {...gridProps(grid)}
        />
        )}
      </Panel>
    )
  }

  /*
    The blocks in the definition's order (form-layout.ts): sections before the
    grids unless one is placed after the lines, a grid after the section it
    names, or the explicit `formOrder` a project needs.
  */
  const renderBlock = (block: FormBlock): ReactNode => {
    switch (block.kind) {
      case 'section': return renderSection(block.section)
      case 'lines': return renderPrimary(block.grid)
      case 'grid': return renderGrid(block.grid)
      case 'totalValue':
        return doc?.totalValue ? (
          <TotalValuePanel
            key="totalValue"
            value={doc.totalValue}
            editable={!readOnly && !leftDraft}
            typed={String(watched['roundingOverride'] ?? '')}
            onRounding={(v) => form.setValue('roundingOverride', v, { shouldDirty: true })}
          />
        ) : null
      case 'projectFigures':
        // The project's running totals, recomputed from the grids as they are typed.
        return def.key === 'project' ? (
          <LiveProjectFigures key="projectFigures" lines={lines} interstate={watched['interstate'] === true || watched['interstate'] === 'true'} />
        ) : null
      case 'slot':
        return slots?.[block.name] !== undefined ? <div key={block.key}>{slots[block.name]}</div> : null
    }
  }

  return <>{formBlocks(def, sections).map(renderBlock)}</>
}

/**
 * A row's own grid, folded under it until asked for — three hundred BOQ
 * lines each with their estimate open would be a page per line.
 */
const NestedGrid = ({ title, count, children }: { title: string; count: number; children: ReactNode }) => {
  const [open, setOpen] = useState(false)
  return (
    <div className="nested-grid">
      <button type="button" className="link-button" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        {open ? '▾' : '▸'} {title} · {count === 0 ? 'none yet' : `${count} line${count === 1 ? '' : 's'}`}
      </button>
      {open ? <div style={{ marginTop: 8 }}>{children}</div> : null}
    </div>
  )
}

/**
 * Pulls the parent document's still-open lines into this one.
 *
 * This is the only way an allocation gets created from the interface. Without
 * it the ledger — the thing the whole architecture is built around — was
 * reachable only through the API, and "Ordered" and "Still open" were blank on
 * every line a person typed.
 */
export const PullSourceLines = ({
  docKey, documentId, label, existing, onPulled,
}: {
  docKey: string
  documentId: string
  label: string
  existing: readonly LineRow[]
  onPulled: (lines: LineRow[]) => void
}) => {
  const api = useApi()
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState<string | null>(null)

  const pull = async (): Promise<void> => {
    setBusy(true)
    setNote(null)
    try {
      const source = await api.sourceLines(docKey, documentId)
      // A request line answered on a transfer is kept as `mrLineId`, not as its source.
      const already = new Set(existing.flatMap((l) => [String(l['fromLineId'] ?? ''), String(l['mrLineId'] ?? '')]))
      // A rate contract's work items have no quantity to be open, and are
      // always offered: progress consumes the rate, the quantity is measured.
      const open = source.filter((l) => (l.rateOnly === true || Number(l.openQty) > 0) && !already.has(l.fromLineId))
      if (open.length === 0) {
        setNote(
          source.length === 0
            ? 'There is no order on this document to pull from yet.'
            : 'Every line on that order is already accounted for.',
        )
        return
      }
      onPulled([
        ...existing,
        ...open.map((l, i) => ({
          seq: existing.length + i + 1,
          fromLineId: l.fromLineId,
          itemId: l.itemId,
          itemLabel: l.itemLabel,
          description: l.description,
          brand: l.brand,
          unit: l.unit,
          rate: l.rate,
          orderedQty: l.orderedQty,
          openQty: l.openQty,
          // Travels with the line so a challan raised from a receipt can be
          // split into what the customer is charged for and what merely has to
          // reach the site.
          isBillable: l.isBillable ? 'YES' : 'NO',
          /*
            What the order actually agreed, not what the item master says.

            Only the item, description, brand, unit, rate and quantities used
            to be copied, so a purchase invoice pulled from an order silently
            lost a line-level HSN override and any discount negotiated on the
            order — silently, because `hsn_missing` falls back to the master
            and blocks nothing.
          */
          ...(l.hsnSacCode ? { hsnSacCode: l.hsnSacCode } : {}),
          ...(l.taxPct !== null && l.taxPct !== undefined ? { taxPct: l.taxPct } : {}),
          ...(l.discountPct !== null && l.discountPct !== undefined ? { discountPct: l.discountPct } : {}),
          ...(l.billingTermLineId ? { billingTermLineId: l.billingTermLineId } : {}),
          // Pre-filled with what is still open; the operator edits it down to
          // what actually turned up. Where a receipt is named, what turned up
          // is already known, so that is the better suggestion.
          quantity: l.rateOnly ? '' : l.receivedQty ?? l.suggestedQty ?? l.openQty,
          // Whatever else the source says the row starts with (docs/24).
          ...(l.carry ?? {}),
        })),
      ])
      setNote(
        `${open.length} line${open.length === 1 ? '' : 's'} pulled in. `
        + (open.some((l) => l.receivedQty !== undefined)
          ? 'The quantity is what the receipt took in.'
          : 'Change the quantity to what actually arrived.'),
      )
    } catch (err) {
      setNote(err instanceof Error ? err.message : 'Could not read the order.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div style={{ marginBottom: 16 }}>
      <Button kind="ghost" onClick={() => { void pull() }} disabled={busy}>
        {busy ? 'Reading the order…' : label}
      </Button>
      {note ? (
        <div style={{ font: '400 14px/1.6 var(--font-body)', color: 'var(--muted)', marginTop: 10 }}>{note}</div>
      ) : null}
    </div>
  )
}
